'use client';

import { useEffect, useState, useRef } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, TransitionChild } from '@headlessui/react';
import {
  Bars3Icon,
  CalendarIcon,
  DocumentDuplicateIcon,
  HomeIcon,
  UsersIcon,
  XMarkIcon,
  UserIcon,
  CalendarDaysIcon,
  BellIcon,
  ArrowPathRoundedSquareIcon,
  Cog6ToothIcon,
  WalletIcon,
  ChartBarIcon,
  DocumentArrowUpIcon,
  PowerIcon,
  ClipboardDocumentCheckIcon,
  DocumentCheckIcon,
  CalendarDateRangeIcon,
  ChartBarSquareIcon,
  QuestionMarkCircleIcon,
  LightBulbIcon,
  BriefcaseIcon,
  ExclamationTriangleIcon,
  ShoppingCartIcon


} from '@heroicons/react/20/solid';
import { BookOpenIcon, ChartPieIcon, CurrencyPoundIcon, DocumentMagnifyingGlassIcon, MagnifyingGlassCircleIcon, ScaleIcon, UserGroupIcon } from '@heroicons/react/24/solid';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Shifts from './shifts';
import ShiftBooking from './bookshifts';
import RoleCalibration from './rolecalibration';
import HolidayApprove from './holidayapprove';
import ActivityFeed from './activitybar';
import MeetingAlert from './meetingalert';
import ShiftHome from './shifthome';
import MeetingsCalendar from './meetingscalendar';
import Stats from './stats';
import Online from './online';
import TaskManager from './taskmanager';
import StaffActions from './staffactions';
import { AcademicCapIcon } from '@heroicons/react/20/solid';
import { FolderIcon, NewspaperIcon, SparklesIcon } from '@heroicons/react/24/outline';
import ManagementHome from './managementhome';
import WeatherData from './weatherdata';
import AIAssistant from './aiassistant';
import BackToWorkAlert from './backtoworkalert';
import NewsHome from './newshome';
import { ChatBubbleBottomCenterIcon } from '@heroicons/react/16/solid';
import { useContext } from 'react';
import { AuthContext } from '../AuthContext';
import MessageAlert from './messagealert';
import TaskHome from './taskhome';
import ShiftsAlert from './shiftsstartalert';
import AIChatbot from './aichatbotreadonly';
import HasHR from './hasHR';
import Avenor from './avenor';
import { differenceInCalendarDays, format } from 'date-fns';






const tabs = [
  { name: 'Stats', component: <Stats />, icon: ChartPieIcon },
  { name: 'Management', component: <ManagementHome />, icon: BookOpenIcon },

  { name: 'Staff', component: <ShiftHome />, icon: UserIcon },
  { name: 'Calendar', component: <MeetingsCalendar />, icon: CalendarDaysIcon },
  { name: 'Task Manager', component: <TaskHome />, icon: Cog6ToothIcon },
  { name: 'News Feed', component: <NewsHome />, icon: NewspaperIcon },


];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const slugify = (value = '') =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const buildNavTracking = (name, placement) => {
  const slug = slugify(name);
  return {
    component: `dashboard-nav-${placement}-${slug || 'unknown'}`,
    feature: `nav_${slug || 'unknown'}`,
  };
};

/**
 * Admin Dashboard component.
 * 
 * @component
 * @description Renders the main administrative interface with responsive sidebar navigation,
 *              tabbed content area, weather widget, and activity feed. Includes mobile-optimized
 *              navigation with a dialog-based sidebar. Fetches and displays user messages from the API.
 *              
 * @returns {JSX.Element} The rendered Admin dashboard interface or AI Assistant component
 *                        based on the AI state flag.
 * 
 * @example
 * return (
 *   <Admin />
 * )
 */
export default function Admin() {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(tabs[0].name); // Default to the first tab
  const [modalOpen, setModalOpen] = useState(false);
  const [mobileActivityOpen, setMobileActivityOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [hrMode, setHR] = useState(false);
  const [avenorMode, setAvenorMode] = useState(false);
  const aiChatRef = useRef(null);

  const renderComponent = () => {
    if (avenorMode) {
      return <Avenor user={user} onBack={() => setAvenorMode(false)} />;
    }

    if (hrMode) {
      return <HasHR onBack={() => setHR(false)} />;
    }

    const currentTabData = tabs.find((tab) => tab.name === currentTab);
    return currentTabData ? currentTabData.component : null;
  };

  useEffect(() => {
    const requestedTab = location.state?.dashboardTab;
    if (!requestedTab) {
      return;
    }

    if (tabs.some((tab) => tab.name === requestedTab)) {
      setCurrentTab(requestedTab);
    }
  }, [location.state?.dashboardTab]);

  const navigate = useNavigate();

  const user = useContext(AuthContext);
  const canAccessAvenor = ['admin', 'manager', 'staff'].includes(String(user?.access || '').toLowerCase());

  const isTrialUser = user?.trial === true || user?.trial === 1 || user?.trial === 'true' || user?.trial === '1';
  const isAdminAccess = String(user?.access || '').toLowerCase() === 'admin';
  const showTrialBanner = isTrialUser && isAdminAccess;
  const trialEndDate = user?.trialEnd ? new Date(user.trialEnd) : null;
  const hasValidTrialEndDate = trialEndDate && !Number.isNaN(trialEndDate.getTime());
  const trialEndLabel = hasValidTrialEndDate ? format(trialEndDate, 'dd/MM/yyyy') : 'N/A';
  const trialDaysRemaining = hasValidTrialEndDate ? differenceInCalendarDays(trialEndDate, new Date()) : null;
  const trialDaysText = trialDaysRemaining === null
    ? 'Trial end date unavailable'
    : trialDaysRemaining >= 0
      ? `${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'} remaining`
      : `${Math.abs(trialDaysRemaining)} day${Math.abs(trialDaysRemaining) === 1 ? '' : 's'} overdue`;

  

console.log(user);


  // if the user is not onboarded, redirect to onboarding
  useEffect(() => {

const getOnboardedStep = async () => {
      try {
        const response = await fetch('https://api.insightos.co.uk/api/onboardingstep', {
          method: 'GET',
          credentials: 'include',
        });
        const data = await response.json();
        if (data.success) {
          const { step } = data;

          console.log('Onboarded Step:', step);

          // if the step is below 5, redirect to onboarding
          if (step < 11) {
            navigate('/onboarding');
          }

          // if above 11 but below 16 i want a modal to show the user they need to complete onboarding
          if (step >= 11 && step < 16) {

            setModalOpen(true);
          }

          // if its 7 then ignore completley

          if (step >= 17) {
            // User is fully onboarded, no action needed
          }
        }
      } catch (error) {
        console.error('Failed to fetch onboarded step', error);
      }
    };

    getOnboardedStep()



    
  }, [navigate]);



const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, current: true, feature: 'nav_dashboard' },
  { name: 'Chat', href: '/chatdashboard', icon: ChatBubbleBottomCenterIcon, current: false, feature: 'nav_chat' },
  { name: 'My Profile', href: '/myprofile', icon: UserIcon, current: false, feature: 'nav_profile' },
  { name: 'Projects', href: '/project-management', icon: ChartBarSquareIcon, current: false, feature: 'nav_projects' },
  { name: 'Brainstorm', href: '/brainstorm', icon: SparklesIcon, current: false, feature: 'nav_brainstorm' },
    { name: 'Finances', href: '/finances', icon: CurrencyPoundIcon, current: false, feature: 'nav_finances' },

  { name: 'Sales / Project CRM', href: '/customercrm', icon: UserGroupIcon, current: false, feature: 'nav_customer_crm' },
  { name: 'Task Manager', href: '/taskcalendar', icon: CalendarDateRangeIcon, current: false, feature: 'nav_task_manager' },
  { name: 'Recruitment', href: '/recruitment', icon: BriefcaseIcon, current: false, feature: 'nav_recruitment' },
  { name: 'Admin Tasks', href: '/admin-tasks', icon: DocumentCheckIcon, current: false, feature: 'nav_admin_tasks' },
  { name: 'Training', href: '/training', icon: AcademicCapIcon, current: false, feature: 'nav_training' },
  { name: 'Organization', href: '/organizationaltree', icon: ScaleIcon, current: false, feature: 'nav_organization' },
  { name: 'Users', href: '/users', icon: UserGroupIcon, current: false, feature: 'nav_users' },
  { name: 'Billing', href: '/billing', icon: WalletIcon, current: false, feature: 'nav_billing' },
  { name: 'Docs', href: '/support', icon: DocumentMagnifyingGlassIcon, current: false, feature: 'nav_docs' },
  { name: 'Marketplace', href: '/marketplace', icon: ShoppingCartIcon, current: false, feature: 'nav_marketplace' },
  { name: 'Productivity Insights', href: '/productivity-insights', icon: MagnifyingGlassCircleIcon, current: false, feature: 'nav_productivity_insights' },
  { name: 'Log Out', href: '/logout', icon: PowerIcon, current: false, feature: 'nav_logout' },
];

          


  
  // Handler to close the AI Assistant
  
  
  // Check if the device is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Check initially
    checkIfMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkIfMobile);
    
    // Clean up
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Handler to close mobile activity feed when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const activityFeedContainer = document.getElementById('mobile-activity-feed');
      const activityToggleButton = document.getElementById('activity-toggle-button');
      
      if (
        mobileActivityOpen &&
        activityFeedContainer && 
        !activityFeedContainer.contains(event.target) &&
        activityToggleButton &&
        !activityToggleButton.contains(event.target)
      ) {
        setMobileActivityOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileActivityOpen]);

  return (
    <>
      <div>
      <div className="flex h-screen" data-component="dashboard" data-feature="layout_shell">
      {/* Sidebar (Fixed for Desktop) */}
      <div className="hidden lg:flex lg:flex-col lg:w-[80px] lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:bg-gradient-to-b from-indigo-900 via-sky-500 to-emerald-500 lg:pb-4 overflow-y-auto">
        <div className="flex h-16 items-center justify-center mt-2">
          <img alt="Logo" src="/2.png" className="h-10 w-auto rounded-full bg-gray-900" />
        </div>
            {canAccessAvenor && (
              <div className="mt-2 flex items-center justify-center">
                <button
                  type="button"
                  onClick={() => {
                    navigate('/avenor');
                  }}
                  className="group relative flex items-center justify-center p-2 rounded-md text-white hover:bg-blue-700"
                  data-component="dashboard-nav-desktop-avenor"
                  data-feature="nav_avenor"
                  aria-label="Avenor"
                >
                  <img src="/avenor.png" alt="Avenor" className="h-8 w-8 bg-white object-contain" />
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 scale-0 rounded-md bg-gray-700 px-3 py-1 text-xs text-white shadow-lg transition-all duration-150 group-hover:scale-100">
                    Avenor
                  </span>
                </button>
              </div>
            )}
        <nav className="mt-8 flex flex-col items-center space-y-1">
          {navigation.map((item) => {
            const tracking = buildNavTracking(item.name, 'desktop');
            return (
              <div key={item.name} className="relative">
                <Link
                  to={item.href}
                  data-component={tracking.component}
                  data-feature={tracking.feature}
                  className={`group flex items-center justify-center p-3 rounded-md ${
                    item.current ? "bg-white text-blue-700" : "text-white hover:bg-blue-700"
                  }`}
                >
                  <item.icon className="h-6 w-6" />
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 scale-0 rounded-md bg-gray-700 px-3 py-1 text-xs text-white shadow-lg transition-all duration-150 group-hover:scale-100">
                    {item.name}
                  </span>
                </Link>
              </div>
            );
          })}
        </nav>
      </div>

      {/* Sidebar (Mobile - Dialog) */}
      <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="lg:hidden">
        <DialogBackdrop className="fixed inset-0 bg-white opacity-75 transition-opacity" />
        <div className="fixed inset-0 flex">
          <DialogPanel className="w-full max-w-xs transform transition-all duration-300">
            <div className="bg-gradient-to-b from-indigo-900 via-sky-500 to-emerald-500 p-6 h-full flex flex-col">
              <div className="flex justify-between items-center">
                <img alt="Logo" src="/1.png" className="h-8 rounded-full" />
                <button onClick={() => setSidebarOpen(false)}>
                  <XMarkIcon className="h-6 w-6 text-white" />
                </button>
              </div>
              {canAccessAvenor && (
                <button
                  type="button"
                  onClick={() => {
                    navigate('/avenor');
                    setSidebarOpen(false);
                  }}
                  className="mt-4 flex items-center text-white hover:bg-blue-700 p-5 rounded-md"
                  data-component="dashboard-nav-mobile-avenor"
                  data-feature="nav_avenor"
                >
                  <img src="/avenor.png" alt="Avenor" className="h-6 w-6 rounded-md object-contain flex-shrink-0" />
                  <span className="ml-3">Avenor</span>
                </button>
              )}
              <nav className="mt-8 flex-1 overflow-y-auto">
                <ul className="space-y-2 pb-20"> {/* Added padding at bottom for better scrolling */}
                  {navigation.map((item) => {
                    const tracking = buildNavTracking(item.name, 'mobile');
                    return (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          data-component={tracking.component}
                          data-feature={tracking.feature}
                          className="flex items-center text-white hover:bg-blue-700 p-3 rounded-md"
                          onClick={() => setSidebarOpen(false)}
                        >
                          <item.icon className="h-6 w-6 flex-shrink-0" />
                          <span className="ml-3">{item.name}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Main Content Wrapper */}
      <div className="flex flex-1 flex-col lg:ml-[80px] overflow-hidden">
        {/* Top Navbar (Mobile Only) */}
        <div className="sticky top-0 z-40 bg-gradient-to-l from-indigo-900 via-sky-500 to-emerald-500 backdrop-blur-2xl border-b border-white/20 px-4 py-2 shadow-sm lg:hidden">
          <div className="flex items-center">
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-white"
              data-component="dashboard-toolbar"
              data-feature="open_sidebar"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
            <div className="flex-1 text-white font-semibold ml-3">Dashboard</div>
            <button 
              id="activity-toggle-button"
              onClick={() => setMobileActivityOpen(!mobileActivityOpen)}
              className="text-white mr-3 relative"
              aria-label="Toggle sidebar menu"
              data-component="dashboard-toolbar"
              data-feature="toggle_activity_panel"
            >
              <ChartBarSquareIcon className="h-6 w-6" />
              <span className="absolute -top-1 -right-1 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-sky-500"></span>
              </span>
            </button>
            {isMobile && (
              <AIChatbot
                isMobile={isMobile}
                buttonFixed={false}
                buttonClassName="text-white rounded-full hover:bg-blue-700"
                buttonSizeClass="w-6 h-6"
                iconClassName="h-4 w-4"
              />
            )}
            <Link 
              to="/logout" 
              className="flex items-center justify-center text-white p-1 rounded-full hover:bg-blue-700"
              aria-label="Logout"
              data-component="dashboard-toolbar"
              data-feature="logout_quick"
            >
              <PowerIcon className="h-6 w-6" />
            </Link>
          </div>

          {showTrialBanner && (
            <div className="mt-2 rounded-lg bg-red-500 px-3 py-2 text-white shadow-md">
              <p className="text-[11px] font-semibold leading-4">
                Trial version limited to 5 users • Ends {trialEndLabel} • {trialDaysText}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => navigate('/paymenttrial', { state: { companyCode: user?.companyCode } })}
                  className="rounded-md bg-white px-2.5 py-1 text-[11px] font-semibold text-red-600"
                >
                  Upgrade Now
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/sales-training')}
                  className="rounded-md border border-white/80 px-2.5 py-1 text-[11px] font-semibold text-white"
                >
                  Speak To Sales
                </button>
              </div>
            </div>
          )}

          {!hrMode && !avenorMode && (
            <div dir="rtl" className="mt-2 overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div dir="ltr" className="mx-auto flex w-max min-w-full items-center justify-center gap-2 px-1">
                {tabs.map((tab) => {
                  const MobileTabIcon = tab.icon;
                  const active = tab.name === currentTab;
                  const tabFeature = `mobile_tab_${tab.name.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;

                  return (
                    <button
                      key={tab.name}
                      type="button"
                      onClick={() => setCurrentTab(tab.name)}
                      aria-label={tab.name}
                      title={tab.name}
                      data-component="dashboard-mobile-tab"
                      data-feature={tabFeature}
                      className={`h-9 w-9 inline-flex items-center justify-center rounded-lg border transition-all ${
                        active
                          ? 'bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 border-white/20 text-white shadow-lg shadow-indigo-950/40'
                          : 'bg-white/55 border-indigo-200/70 text-indigo-900 hover:bg-white/75'
                      }`}
                    >
                      <MobileTabIcon className="h-3.5 w-3.5" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        
        {/* Mobile Full Screen Sidebar */}
        <AnimatePresence>
          {mobileActivityOpen && (
            <motion.div 
              id="mobile-activity-feed"
              initial={{ opacity: 0, x: '100%' }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: '100%' }}
              transition={{ duration: 0.3 }}
              className="lg:hidden fixed inset-0 z-50 bg-white overflow-y-auto max-h-full"
              style={{ overscrollBehavior: 'contain' }}
              data-component="dashboard-mobile-panel"
              data-feature="activity_feed"
            >
              <div className="flex flex-col h-full">
                <div className="sticky top-0 bg-white z-10 p-4 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    <h3 className="font-semibold text-gray-800 flex items-center">
                      <ChartBarSquareIcon className="h-5 w-5 mr-2 text-blue-500" />
                      Dashboard Menu
                    </h3>
                    <button 
                      onClick={() => setMobileActivityOpen(false)}
                      className="text-gray-500 hover:text-gray-700 bg-gray-100 rounded-full p-1"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                  <WeatherData />
                
                  {user.hasHR ? (
                    <div className="mt-4 rounded-2xl bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 backdrop-blur-xl border border-indigo-200/60 shadow-[0_12px_30px_rgba(99,102,241,0.2)] p-3">
                      <div className="flex items-center justify-between gap-3">
                        <motion.button
                          type="button"
                          onClick={() => {
                            navigate('/createmeeting');
                            setMobileActivityOpen(false);
                          }}
                          className="h-12 w-12 inline-flex items-center justify-center rounded-xl bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 border border-white/15 text-white shadow-lg shadow-indigo-950/40 hover:from-indigo-800/95 hover:to-indigo-600/95 transition-all"
                          data-component="dashboard-mobile-actions"
                          data-feature="create_meeting"
                          whileHover={{ scale: 1.12 }}
                          whileTap={{ scale: 0.97 }}
                        >
                          <CalendarIcon className="h-6 w-6" />
                        </motion.button>
                        <motion.button
                          type="button"
                          onClick={() => {
                            navigate('/support');
                            setMobileActivityOpen(false);
                          }}
                          className="h-12 w-12 inline-flex items-center justify-center rounded-xl bg-gradient-to-br from-sky-600/90 to-sky-500/90 border border-white/15 text-white shadow-lg shadow-sky-900/35 hover:from-sky-500/95 hover:to-sky-400/95 transition-all"
                          data-component="dashboard-mobile-actions"
                          data-feature="open_support"
                          whileHover={{ scale: 1.12 }}
                          whileTap={{ scale: 0.97 }}
                        >
                          <LightBulbIcon className="h-6 w-6" />
                        </motion.button>
                        <motion.button
                          type="button"
                          onClick={() => {
                            setAvenorMode(false);
                            setHR(true);
                            setMobileActivityOpen(false);
                          }}
                          className="h-12 w-12 inline-flex items-center justify-center rounded-xl bg-gradient-to-br from-emerald-600/90 to-emerald-500/90 border border-white/15 text-white shadow-lg shadow-emerald-900/35 hover:from-emerald-500/95 hover:to-emerald-400/95 transition-all"
                          data-component="dashboard-mobile-actions"
                          data-feature="open_hr"
                          whileHover={{ scale: 1.12 }}
                          whileTap={{ scale: 0.97 }}
                        >
                          HR
                        </motion.button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 rounded-2xl bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 backdrop-blur-xl border border-indigo-200/60 shadow-[0_12px_30px_rgba(99,102,241,0.2)] p-3">
                      <div className="flex items-center justify-between gap-3">
                        <motion.button
                          type="button"
                          onClick={() => {
                            navigate('/createmeeting');
                            setMobileActivityOpen(false);
                          }}
                          className="h-12 w-12 inline-flex items-center justify-center rounded-xl bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 border border-white/15 text-white shadow-lg shadow-indigo-950/40 hover:from-indigo-800/95 hover:to-indigo-600/95 transition-all"
                          data-component="dashboard-mobile-actions"
                          data-feature="create_meeting"
                          whileHover={{ scale: 1.12 }}
                          whileTap={{ scale: 0.97 }}
                        >
                          <CalendarIcon className="h-6 w-6" />
                        </motion.button>
                        <motion.button
                          type="button"
                          onClick={() => {
                            navigate('/support');
                            setMobileActivityOpen(false);
                          }}
                          className="h-12 w-12 inline-flex items-center justify-center rounded-xl bg-gradient-to-br from-sky-600/90 to-sky-500/90 border border-white/15 text-white shadow-lg shadow-sky-900/35 hover:from-sky-500/95 hover:to-sky-400/95 transition-all"
                          data-component="dashboard-mobile-actions"
                          data-feature="open_support"
                          whileHover={{ scale: 1.12 }}
                          whileTap={{ scale: 0.97 }}
                        >
                          <LightBulbIcon className="h-6 w-6" />
                        </motion.button>
                      </div>
                    </div>
                  )}
                  {/* Activity Feed */}
                  <div className="mt-4 pb-20"> {/* Added padding at the bottom */}
                    <ActivityFeed mobileView={true} />
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Content Layout */}
        <div className="flex h-full">
          {/* Sidebar for Extra Features - Hidden on mobile */}
          <div
            className="hidden md:block w-1/3 lg:w-1/3 xl:w-1/3 border-r border-gray-200 p-6 overflow-y-auto"
            data-component="dashboard-sidebar"
            data-feature="insights_panel"
          >
            <WeatherData />

            {showTrialBanner && (
              <div className="mt-4 hidden md:block rounded-xl bg-red-500 px-3 py-3 text-white shadow-md">
                <p className="text-xs font-semibold leading-5">
                  Trial version limited to 5 users
                </p>
                <p className="mt-1 text-xs leading-5">
                  Ends {trialEndLabel} • {trialDaysText}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => navigate('/paymenttrial', { state: { companyCode: user?.companyCode } })}
                    className="rounded-md bg-white px-2.5 py-1 text-[11px] font-semibold text-red-600"
                  >
                    Upgrade Now
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/sales-training')}
                    className="rounded-md border border-white/80 px-2.5 py-1 text-[11px] font-semibold text-white"
                  >
                    Speak To Sales
                  </button>
                </div>
              </div>
            )}

            {user.hasHR ? (
              <div className="mt-4 rounded-2xl bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 backdrop-blur-xl border border-indigo-200/60 shadow-[0_12px_30px_rgba(99,102,241,0.2)] p-3">
                <div className="flex items-center justify-between gap-3">
                  <motion.button
                    type="button"
                    onClick={() => navigate('/createmeeting')}
                    className="h-12 w-12 inline-flex items-center justify-center rounded-xl bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 border border-white/15 text-white shadow-lg shadow-indigo-950/40 hover:from-indigo-800/95 hover:to-indigo-600/95 transition-all"
                    data-component="dashboard-sidebar-actions"
                    data-feature="create_meeting"
                    whileHover={{ scale: 1.12 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <CalendarIcon className="h-6 w-6" />
                  </motion.button>
                  <motion.button
                    type="button"
                    onClick={() => navigate('/support')}
                    className="h-12 w-12 inline-flex items-center justify-center rounded-xl bg-gradient-to-br from-sky-600/90 to-sky-500/90 border border-white/15 text-white shadow-lg shadow-sky-900/35 hover:from-sky-500/95 hover:to-sky-400/95 transition-all"
                    data-component="dashboard-sidebar-actions"
                    data-feature="open_support"
                    whileHover={{ scale: 1.12 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <LightBulbIcon className="h-6 w-6" />
                  </motion.button>
                  <motion.button
                    type="button"
                    onClick={() => {
                      setAvenorMode(false);
                      setHR(true);
                    }}
                    className="h-12 w-12 inline-flex items-center justify-center rounded-xl bg-gradient-to-br from-emerald-600/90 to-emerald-500/90 border border-white/15 text-white shadow-lg shadow-emerald-900/35 hover:from-emerald-500/95 hover:to-emerald-400/95 transition-all"
                    data-component="dashboard-sidebar-actions"
                    data-feature="open_hr"
                    whileHover={{ scale: 1.12 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    HR
                  </motion.button>
                </div>
              </div>
            ) : (
              <div className="mt-4 flex items-center">
                <button
                  type="button"
                  onClick={() => navigate('/createmeeting')}
                  className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  data-component="dashboard-sidebar-actions"
                  data-feature="create_meeting"
                >
                  <CalendarIcon className="h-6 w-6" />
                  Create Meeting
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/support')}
                  className="inline-flex ml-2 items-center gap-x-1.5 rounded-md bg-emerald-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  data-component="dashboard-sidebar-actions"
                  data-feature="open_support"
                >
                  <LightBulbIcon className="h-6 w-6" />
                  Support
                </button>
              </div>
            )}
            {/* Desktop Activity Feed */}
            <div className="mt-4">
              <ActivityFeed />
            </div>
          </div>

          {/* Main Content */}
          <div
            className="w-full md:w-2/3 lg:w-3/4 xl:w-4/5 overflow-y-auto p-2 min-w-0"
            data-component="dashboard-main"
            data-feature="content_area"
          >
            {/* Tabs for Larger Screens */}
            {!hrMode && !avenorMode && (
            <div className="hidden sm:block">
              <nav
                aria-label="Tabs"
                className="isolate flex overflow-x-auto flex-nowrap gap-1 xl:gap-2 lg:gap-1 md:gap-1 rounded-2xl shadow-lg bg-gradient-to-r from-blue-50 via-white to-purple-50 p-1 hide-scrollbar"
                style={{ WebkitOverflowScrolling: 'touch' }}
              >
                {tabs.map((tab) => {
                  const tabFeature = `tab_${tab.name.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;
                  return (
                    <motion.button
                      key={tab.name}
                      onClick={() => setCurrentTab(tab.name)}
                      aria-current={tab.name === currentTab ? "page" : undefined}
                      data-component="dashboard-tab"
                      data-feature={tabFeature}
                      className={`group relative min-w-[80px] xl:min-w-[110px] lg:min-w-[100px] md:min-w-[90px] flex-1 overflow-hidden rounded-lg xl:rounded-xl shadow bg-white px-2 xl:px-3 lg:px-2 md:px-2 py-2 xl:py-2 lg:py-2 md:py-1 text-center text-xs xl:text-sm lg:text-xs md:text-[11px] font-medium hover:bg-blue-700 transition-all duration-200 ${
                        tab.name === currentTab
                          ? "text-blue-900 bg-blue-100 border-2 border-blue-500"
                          : tab.hasUnreadMessages
                          ? "text-blue-700 bg-blue-50 border-2 border-blue-300 animate-pulse"
                          : "text-blue-700 hover:text-white hover:bg-blue-700"
                      }`}
                      whileTap={{ scale: 0.97 }}
                      whileHover={{ scale: 1.04 }}
                      animate={
                        tab.hasUnreadMessages
                          ? {
                              backgroundColor: ["#f0f7ff", "#0023bc", "#f0f7ff"],
                              scale: [1, 1.05, 1],
                            }
                          : {}
                      }
                      transition={{ duration: 1.5, repeat: tab.hasUnreadMessages ? Infinity : 0 }}
                    >
                      <span className="ml-2">{tab.name}</span>
                      {tab.hasUnreadMessages && (
                        <span className="absolute top-1 right-1 block w-2.5 h-2.5 rounded-full bg-blue-700 shadow-lg" />
                      )}
                    </motion.button>
                  );
                })}
              </nav>
              <style>{`
                .hide-scrollbar::-webkit-scrollbar { display: none; }
                .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
              `}</style>
            </div>
            )}

            {/* Content Rendering */}
            <div className="px-4 py-10 sm:px-6 lg:px-8 lg:py-6">
              <AnimatePresence>
                {renderComponent() && (
                  <motion.div
                    key={currentTab}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3 }}
                  >
                    {renderComponent()}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
   
    {modalOpen && (
    <div className="fixed top-4 md:right-4 z-50 w-full md:w-auto px-4 md:px-0">
       <div className="rounded-md bg-yellow-50 p-4">
      <div className="flex">
        <div className="shrink-0">
          <ExclamationTriangleIcon aria-hidden="true" className="size-5 text-yellow-400" />
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-yellow-800">Onboarding is incomplete</h3>
          <div className="mt-2 text-sm text-yellow-700">
            <p>
              Please complete all onboarding steps to ensure a smooth experience. 
            </p>
            <button
              onClick={() => navigate('/onboarding')}
              className="mt-2 inline-flex items-center px-3 py-1.5 text-sm font-medium text-yellow-800 bg-yellow-100 border border-transparent rounded-md hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
            >
              Go to Onboarding
            </button>

          </div>
        </div>
      </div>
    </div>
    </div>
    )}

<div className="fixed top-4 w-full md:w-auto md:right-4 z-50 px-4 md:px-0">
  <MeetingAlert />
</div>

<div className="fixed top-16 w-full md:w-auto md:right-4 md:top-4 z-50 px-4 md:px-0">
  <MessageAlert />
</div>

<div className="fixed top-28 w-full md:w-auto md:right-4 md:top-4 z-50 px-4 md:px-0">
  <ShiftsAlert />
</div>

{!isMobile && (
  <div ref={(el) => {
    if (el) {
      const button = el.querySelector('button');
      if (button) aiChatRef.current = button;
    }
  }}>
    <AIChatbot isMobile={isMobile} buttonFixed={true} />
  </div>
)}
    </>
  );
}
